/**
 * Reload Loop Breaker Plugin
 *
 * Detects and breaks infinite reload loops caused by service worker updates,
 * particularly on iOS Safari.
 *
 * **Server-side**: Injects inline script into <head> that tracks reloads via
 * sessionStorage. If >3 SSR page loads of the same URL (path+query) in
 * 1 minute, performs nuclear reset (clear all caches, unregister all SWs,
 * reload) and stashes diagnostics.
 *
 * All SSR load types count (reload, navigate, back_forward). Navigating
 * to a different URL wipes the counter so back/forward starts fresh.
 *
 * **Client-side**: Reports stashed diagnostics to Sentry after the clean reload.
 *
 * Pattern follows `plugins/early-error-capture.ts`.
 */

declare global {
  interface Window {
    __reloadLoopDetected?: boolean
  }
}

export default defineNuxtPlugin((nuxtApp) => {
  // SERVER-SIDE: Inject reload loop detection script into <head>
  if (process.server) {
    injectBreakerScript(nuxtApp)
    return
  }

  // CLIENT-SIDE: Report stashed diagnostics to Sentry
  reportReloadLoop(nuxtApp)
})

/**
 * Server-side: Inject inline <head> script for reload loop detection.
 * Must run before any other JS to catch loops that happen before hydration.
 */
function injectBreakerScript(nuxtApp: any) {
  const alfredVersion = useRuntimeConfig().public.alfredVersion || 'unknown'

  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call -- Nuxt app hooks are untyped
  nuxtApp.hooks.hook('app:rendered', () => {
    useHead({
      script: [
        {
          id: 'reload-loop-breaker',
          tagPriority: -10, // Must run before any other JS
          innerHTML: `
(function() {
  try {
    var KEY = '__reload_ts';
    var VH_KEY = '__alfred_version_history';
    var VH_MAX = 10;
    var RL_HISTORY_KEY = '__reload_loop_incidents';
    var RL_HISTORY_MAX = 10;
    var WINDOW_MS = 60000;
    var THRESHOLD = 3;
    var now = Date.now();
    var VERSION = ${JSON.stringify(alfredVersion)};

    // Test mode: ?testReloadLoop=true seeds fake timestamps to trigger breaker
    var testMode = window.location.search.indexOf('testReloadLoop=true') !== -1;

    // --- Detect reload source ---
    // Known sources: pwa-update, loop-breaker, chunk-error, reload-util, map-error, payment-retry, clear-data
    // 'untagged' means no source was set — could be manual F5, browser recovery, or untagged code
    var reloadSource = 'untagged';
    try {
      var src = sessionStorage.getItem('__reload_source');
      if (src) { reloadSource = src; sessionStorage.removeItem('__reload_source'); }
    } catch (e) {}

    // --- Navigation type: reload, navigate, back_forward, prerender ---
    var navType = 'unknown';
    try {
      var navEntry = performance.getEntriesByType('navigation')[0];
      if (navEntry) navType = navEntry.type;
    } catch (e) {}

    var currentUrl = window.location.pathname + window.location.search;

    // --- Reload history: each entry is {t, v, s, n, p} (timestamp, version, source, navType, url) ---
    var raw = sessionStorage.getItem(KEY);
    var entries = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(entries)) entries = [];

    // Migrate legacy format (bare timestamps) to {t, v, s} entries
    if (entries.length > 0 && typeof entries[0] === 'number') {
      entries = entries.map(function(ts) { return {t: ts, v: 'unknown', s: 'unknown'}; });
    }

    // In test mode, inject fake recent entries to exceed the threshold
    if (testMode) {
      entries = [];
      for (var ti = 0; ti < THRESHOLD; ti++) {
        entries.push({t: now - (ti + 1) * 1000, v: VERSION, s: 'test', n: 'reload', p: currentUrl});
      }
      console.warn('[Reload Loop] Test mode — injecting fake entries to trigger breaker');
    }

    // Filter to recent entries within the time window
    var recent = [];
    for (var i = 0; i < entries.length; i++) {
      if (now - entries[i].t < WINDOW_MS) recent.push(entries[i]);
    }

    // If URL changed from the last entry, wipe the counter entirely.
    // Navigating to a different page means the reload state is irrelevant.
    // Back/forward to a previous page starts with a clean slate.
    // Legacy entries without .p skip this check (conservative — they count toward threshold).
    var lastEntry = recent.length > 0 ? recent[recent.length - 1] : null;
    if (lastEntry && lastEntry.p && lastEntry.p !== currentUrl) {
      recent = [];
    }

    // Count all SSR page loads for the same URL toward the threshold.
    // Any full page load (reload, navigate, back_forward) to the same URL counts —
    // the URL wipe above already handles the false-positive case of A→B→A browsing.
    var reloadCount = recent.length;

    // --- Version history (localStorage): append on build version change ---
    try {
      var vhRaw = localStorage.getItem(VH_KEY);
      var vh = vhRaw ? JSON.parse(vhRaw) : [];
      if (!Array.isArray(vh)) vh = [];
      var lastVh = vh.length > 0 ? vh[vh.length - 1] : null;
      if (!lastVh || lastVh.build !== VERSION) {
        vh.push({build: VERSION, runtime: null, t: new Date().toISOString()});
        while (vh.length > VH_MAX) vh.shift();
        localStorage.setItem(VH_KEY, JSON.stringify(vh));
      }
    } catch (e) { /* localStorage unavailable */ }

    if (reloadCount >= THRESHOLD) {
      // Reload loop detected — nuclear reset
      window.__reloadLoopDetected = true;

      // Stop the page from loading further scripts/resources while we do async cleanup.
      // Without this, Nuxt can boot and fail mid-hydration when caches are pulled out from under it.
      try { window.stop(); } catch (e) {}

      // Swallow ALL errors to prevent Nuxt's error page from showing during cleanup.
      // Use capture-phase listeners with stopImmediatePropagation to block every handler,
      // including Vue/Nuxt addEventListener-based error handlers that property-based
      // window.onerror wouldn't catch.
      function swallowError(e) { e.stopImmediatePropagation(); e.preventDefault(); }
      window.addEventListener('error', swallowError, true);
      window.addEventListener('unhandledrejection', swallowError, true);
      window.onerror = function() { return true; };

      console.warn('[Reload Loop] Detected ' + (reloadCount + 1) + ' reloads of ' + currentUrl + ' in 1 min — clearing caches + SWs');

      // --- Record incident in localStorage for repeat-offender tracking ---
      var priorIncidents = 0;
      try {
        var rlRaw = localStorage.getItem(RL_HISTORY_KEY);
        var rlHistory = rlRaw ? JSON.parse(rlRaw) : [];
        if (!Array.isArray(rlHistory)) rlHistory = [];
        priorIncidents = rlHistory.length;
        rlHistory.push({t: new Date().toISOString(), v: VERSION, url: currentUrl, count: reloadCount + 1});
        while (rlHistory.length > RL_HISTORY_MAX) rlHistory.shift();
        localStorage.setItem(RL_HISTORY_KEY, JSON.stringify(rlHistory));
      } catch (e) { /* localStorage unavailable */ }

      // Stash diagnostic context for post-hydration Sentry report
      try {
        var reportReloads = recent.concat({t: now, v: VERSION, s: reloadSource, n: navType, p: currentUrl});
        sessionStorage.setItem('__reload_loop_report', JSON.stringify({
          count: reloadCount + 1,
          reloads: reportReloads,
          priorIncidents: priorIncidents,
          url: window.location.href,
          userAgent: navigator.userAgent,
          referrer: document.referrer
        }));
      } catch (e) { /* storage full — diagnostics lost, reset still happens */ }

      // Clear the reload counter
      sessionStorage.removeItem(KEY);

      // Nuclear reset: delete all caches + unregister all SWs, then reload clean
      var cacheP = window.caches
        ? caches.keys().then(function(keys) {
            return Promise.all(keys.map(function(key) { return caches.delete(key); }));
          })
        : Promise.resolve();

      var swP = navigator.serviceWorker
        ? navigator.serviceWorker.getRegistrations().then(function(regs) {
            return Promise.all(regs.map(function(reg) { return reg.unregister(); }));
          })
        : Promise.resolve();

      Promise.all([cacheP, swP]).catch(function() {}).then(function() {
        // Tag this reload so next page load knows it came from the breaker
        try { sessionStorage.setItem('__reload_source', 'loop-breaker'); } catch(e) {}
        if (testMode) {
          var url = new URL(window.location.href);
          url.searchParams.delete('testReloadLoop');
          window.location.replace(url.toString());
        } else {
          window.location.reload();
        }
      });
      return;
    }

    // No loop — record this entry and continue normally
    recent.push({t: now, v: VERSION, s: reloadSource, n: navType, p: currentUrl});
    sessionStorage.setItem(KEY, JSON.stringify(recent));
    console.warn('[Reload Loop] Load ' + (reloadCount + 1) + '/' + (THRESHOLD + 1) + ' for ' + currentUrl + ' (source: ' + reloadSource + ', nav: ' + navType + ', no action)');
  } catch (e) {
    // sessionStorage unavailable (old Safari private browsing, storage full, etc.)
    // Silently degrade — reload tracking simply doesn't work, which is acceptable.
  }
})();
          `.trim(),
        },
      ],
    })
  })
}

/**
 * Client-side: Read stashed reload loop report from sessionStorage and
 * send it to Sentry. Runs once after the clean reload that follows a
 * nuclear reset.
 */
interface ReloadEntry {
  t: number
  v: string
  s?: string // source: 'pwa-update' | 'loop-breaker' | 'chunk-error' | 'reload-util' | 'map-error' | 'payment-retry' | 'clear-data' | 'untagged'
  n?: string // navType: 'reload' | 'navigate' | 'back_forward' | 'prerender' | 'unknown'
  p?: string // URL (pathname + search)
}

interface ReloadLoopReport {
  count: number
  reloads?: ReloadEntry[]
  priorIncidents?: number
  timestamps?: number[] // Legacy format — remove after v4.47 (sessionStorage, expires with session)
  url: string
  userAgent: string
  referrer: string
}

interface ReloadLoopIncident {
  t: string
  v: string
  url: string
  count: number
}

function reportReloadLoop(nuxtApp: any) {
  if (typeof window === 'undefined') return

  let report: ReloadLoopReport | null = null

  try {
    const raw = sessionStorage.getItem('__reload_loop_report')
    if (!raw) return
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- sessionStorage JSON, validated by null check below
    report = JSON.parse(raw)
    sessionStorage.removeItem('__reload_loop_report')
  } catch {
    return
  }

  if (!report) return

  // Format reload entries — support both new {t,v,s} and legacy [timestamp] formats
  const formatReload = (r: ReloadEntry) =>
    `${r.v} [${r.s || 'unknown'}] ${r.n || 'unknown'} ${r.p || '/'} @ ${new Date(r.t).toISOString()}`
  const reloads = report.reloads
    ? report.reloads.map(formatReload)
    : (report.timestamps?.map((t) => new Date(t).toISOString()) ?? [])

  // Read full incident history from localStorage for repeat-offender context
  let incidentHistory: ReloadLoopIncident[] = []
  try {
    const histRaw = localStorage.getItem('__reload_loop_incidents')
    if (histRaw) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- our own localStorage JSON
      incidentHistory = JSON.parse(histRaw)
    }
  } catch {
    /* localStorage unavailable */
  }

  // Read version history from localStorage (written by inline script before hydration).
  // The version-history plugin sets this as global Sentry context via onNuxtReady,
  // but that hasn't fired yet when this function runs during plugin init.
  let versionHistory: { build: string; runtime: string | null; t: string }[] =
    []
  try {
    const vhRaw = localStorage.getItem('__alfred_version_history')
    if (vhRaw) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- our own localStorage JSON
      versionHistory = JSON.parse(vhRaw)
    }
  } catch {
    /* localStorage unavailable */
  }

  const isRepeat = (report.priorIncidents ?? 0) > 0

  console.error(
    `[Reload Loop] Detected ${report.count} reloads in 1 minute — nuclear reset performed` +
      (isRepeat
        ? ` (repeat offender: ${report.priorIncidents} prior incidents)`
        : ''),
    { count: report.count, reloads, url: report.url },
  )

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- TODO: Type nuxtApp properly
  const sentry = nuxtApp.$sentry

  if (sentry) {
    const error = new Error(
      isRepeat
        ? `Reload loop detected (repeat): ${report.count} reloads in 1 minute`
        : `Reload loop detected: ${report.count} reloads in 1 minute`,
    )
    error.name = isRepeat ? 'ReloadLoopDetectedRepeat' : 'ReloadLoopDetected'

    const lastVersion =
      versionHistory.length > 0
        ? versionHistory[versionHistory.length - 1]
        : null
    const prevVersion =
      versionHistory.length > 1
        ? versionHistory[versionHistory.length - 2]
        : null

    // Extract nav types and sources from reload entries for tags
    const lastReload = report.reloads?.[report.reloads.length - 1]
    const reloadSources = [
      ...new Set(report.reloads?.map((r) => r.s || 'unknown') ?? []),
    ].join(',')

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- TODO: Type sentry properly
    sentry.captureException(error, {
      level: 'error',
      tags: {
        'reload-loop': true,
        'reload-loop-repeat': isRepeat,
        'reload-loop-nav-type': lastReload?.n || 'unknown',
        'reload-loop-source': reloadSources || 'unknown',
        'error-source': 'reload-loop-breaker',
        ...(prevVersion && prevVersion.build !== lastVersion?.build
          ? {
              'version-upgraded': 'true',
              'previous-version': prevVersion.build,
            }
          : {}),
      },
      contexts: {
        versionHistory: {
          current: lastVersion
            ? { build: lastVersion.build, runtime: lastVersion.runtime }
            : null,
          entries: versionHistory.map((e) => `${e.build} @ ${e.t}`),
        },
      },
      extra: {
        reloadCount: report.count,
        reloads,
        priorIncidents: report.priorIncidents ?? 0,
        incidentHistory: incidentHistory.map(
          (i) => `${i.v} x${i.count} ${i.url} @ ${i.t}`,
        ),
        url: report.url,
        userAgent: report.userAgent,
        referrer: report.referrer,
      },
    })
  }
}
